// PWA install helper: captures the browser's install prompt so the app can offer
// its own "Install App" button (login page + dashboard banner).
// Only Chromium browsers (Chrome, Edge, Samsung Internet, Android) fire
// `beforeinstallprompt`; elsewhere the install UI simply stays hidden.
import { useEffect, useState } from 'react';

let deferredPrompt = null;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

export const isRunningInstalled = () => {
    try {
        return window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;
    } catch {
        return false;
    }
};

// Called once from index.js, before React renders, because the browser may fire
// `beforeinstallprompt` before any page component has mounted.
export const initPwaInstall = () => {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault(); // keep the browser's mini-infobar away; we show our own UI
        deferredPrompt = e;
        notify();
    });

    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        notify();
    });
};

// Opens the browser's native install dialog. Returns 'accepted', 'dismissed' or 'unavailable'.
export const promptInstall = async () => {
    if (!deferredPrompt) return 'unavailable';
    const promptEvent = deferredPrompt;
    deferredPrompt = null; // a prompt event can only be used once
    notify();
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    return outcome;
};

// React hook: { canInstall, install }
export const usePwaInstall = () => {
    const [canInstall, setCanInstall] = useState(() => Boolean(deferredPrompt) && !isRunningInstalled());

    useEffect(() => {
        const update = () => setCanInstall(Boolean(deferredPrompt) && !isRunningInstalled());
        listeners.add(update);
        update();
        return () => listeners.delete(update);
    }, []);

    return { canInstall, install: promptInstall };
};
